import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { bankServiceRunService } from '../services/bankServiceRunService';
import type { SirketDto } from '../types/bankServiceRun';

const PAGE_SIZE = 20;

export interface SirketLookupProps {
  /** Seçili şirketin VKN değeri */
  value: string;
  onChange: (vkn: string, serverId?: string) => void;
  serverId?: string;
  tenantId?: string;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  sx?: Record<string, unknown>;
}

/** Input'ta her zaman "Ünvan (VKN)" formatında gösterir; value VKN'dir. */
const getDisplayLabel = (s: SirketDto): string =>
  s.unvan && s.unvan.trim() ? `${s.unvan.trim()} (${s.vkn})` : `— (${s.vkn})`;

export const SirketLookup: React.FC<SirketLookupProps> = ({
  value,
  onChange,
  serverId,
  tenantId,
  label = 'Şirket (VKN)',
  placeholder = 'Şirket ara...',
  size = 'small',
  disabled = false,
  sx,
}) => {
  const [options, setOptions] = useState<SirketDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [selectedServerId, setSelectedServerId] = useState<string | undefined>(undefined);
  /** Kullanıcı dropdown'dan seçim yaptığında veya VKN resolve edildiğinde dolu olan şirket (ünvan + vkn) */
  const [displaySirket, setDisplaySirket] = useState<SirketDto | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPaged = useCallback(
    async (search: string, pageNum: number, append: boolean) => {
      setLoading(true);
      try {
        const result = await bankServiceRunService.getSirketlerPaged({
          serverId,
          tenantId,
          search: search || undefined,
          page: pageNum,
          pageSize: PAGE_SIZE,
        });
        setOptions((prev) =>
          append ? [...prev, ...result.items] : result.items
        );
        setTotalCount(result.totalCount);
        setPage(pageNum);
      } catch {
        if (!append) setOptions([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [serverId, tenantId]
  );

  const handleOpen = useCallback(() => {
    fetchPaged(searchInput, 1, false);
  }, [fetchPaged, searchInput]);

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, inputValue: string) => {
      setSearchInput(inputValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchPaged(inputValue, 1, false);
      }, 300);
    },
    [fetchPaged]
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: SirketDto | null) => {
      setSelectedServerId(newValue?.serverId);
      setDisplaySirket(newValue);
      onChange(newValue?.vkn ?? '', newValue?.serverId);
    },
    [onChange]
  );

  // value (VKN) dışarıdan set edildiğinde (örn. tetikleme popup initialValues) ünvanı API'den çekip göster
  useEffect(() => {
    const vkn = value?.trim();
    if (!vkn || !serverId) {
      if (!vkn) setDisplaySirket(null);
      return;
    }
    if (displaySirket?.vkn === vkn) return;
    if (options.some((s) => s.vkn === vkn)) return;

    setResolveLoading(true);
    bankServiceRunService
      .getSirketlerPaged({
        serverId,
        tenantId,
        search: vkn,
        page: 1,
        pageSize: 50,
      })
      .then((result) => {
        const match = result.items.find((s) => s.vkn === vkn);
        if (match) {
          setDisplaySirket(match);
          setOptions((prev) =>
            prev.some((s) => s.vkn === match.vkn) ? prev : [match, ...prev]
          );
        } else {
          setDisplaySirket({ id: '', serverId, vkn, unvan: '' });
        }
      })
      .catch(() => {
        setDisplaySirket({ id: '', serverId, vkn, unvan: '' });
      })
      .finally(() => {
        setResolveLoading(false);
      });
  }, [value, serverId, tenantId]);

  // value değişince veya temizlenince displaySirket'i senkronize et
  useEffect(() => {
    const vkn = value?.trim();
    if (!vkn) {
      setDisplaySirket(null);
      return;
    }
    if (displaySirket?.vkn === vkn) return;
    const fromOptions = options.find((s) => s.vkn === vkn);
    if (fromOptions) {
      setDisplaySirket(fromOptions);
    }
  }, [value, options, displaySirket?.vkn]);

  const displayValue: SirketDto | null = (() => {
    if (!value?.trim()) return null;
    const vkn = value.trim();
    return (
      options.find((s) => s.vkn === vkn) ??
      (displaySirket?.vkn === vkn ? displaySirket : null) ??
      { id: '', serverId: serverId ?? '', vkn, unvan: '' }
    );
  })();

  return (
    <Autocomplete<SirketDto>
      size={size}
      options={options}
      value={displayValue}
      getOptionLabel={getDisplayLabel}
      isOptionEqualToValue={(a, b) => a.vkn === b.vkn}
      filterOptions={(x) => x}
      loading={loading || resolveLoading}
      disabled={disabled}
      onOpen={handleOpen}
      onInputChange={handleInputChange}
      onChange={handleChange}
      ListboxComponent={(props) => (
        <ul
          {...props}
          role="listbox"
          style={{
            ...props.style,
            maxHeight: 320,
            overflowY: 'auto',
          }}
          onScroll={(e) => {
            const el = e.currentTarget;
            const nearBottom =
              el.scrollHeight - el.scrollTop - el.clientHeight < 60;
            if (
              nearBottom &&
              !loading &&
              options.length < totalCount
            ) {
              fetchPaged(searchInput, page + 1, true);
            }
            props.onScroll?.(e as React.UIEvent<HTMLUListElement>);
          }}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || resolveLoading) ? (
                  <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={loading || resolveLoading ? 'Yükleniyor...' : 'Şirket yok'}
      slotProps={{
        paper: {
          sx: {
            boxShadow: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mt: 0.5,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          },
        },
      }}
      sx={{ flex: { xs: '1 1 100%', sm: '1 1 260px' }, ...sx }}
    />
  );
};
